import { MessageDescriptor, useIntl } from 'react-intl';
import { ErrorMessage, FastField, Field, FieldProps, getIn, useFormikContext } from 'formik';
import { memo, useContext, useEffect, useRef } from 'react';
import Select, { components, MultiValueProps } from 'react-select';
import { Styles } from 'react-select/src/styles';
import { ValueType } from 'react-select/src/types';
import deepmerge from 'deepmerge';
import classNames from 'classnames';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import style from './SelectInput.module.scss';
import { ThemeContext } from '../../../App/App';
import { Questionnaire } from '../../questionnaireHelpers';
import messages from '../../../../messages/feMessages';
import useCountryOptions from '../../../../utilities/useCountryOptions';

interface SelectInputProps {
	name: string;
	label?: MessageDescriptor;
	index?: number;
	isMulti?: boolean;
	isPure?: boolean;
	disabled?: boolean;
	onChange?: (value: string | string[]) => void;
	options: Option[];
	subtitleLabel?: MessageDescriptor;
}

export interface Option {
	label: string | MessageDescriptor;
	value: string;
	dataCy?: MessageDescriptor;
}

function isNarrowScreen(breakpoint = 500) {
	return window.innerWidth < breakpoint;
}

export const defaultSelectStyles: Styles = {
	control: (selectStyle, state) => ({
		...selectStyle,
		borderRadius: 0,
		border: '1px solid #707070',
		marginTop: '0.5rem',
		marginBottom: '0.5rem',
		boxShadow: 'none',
		minHeight: state.isMulti ? '38px' : '32px',
		maxWidth: state.isMulti && !isNarrowScreen() ? '350px' : '250px',
		fontSize: '14px',
		outline: state.isFocused ? '5px auto -webkit-focus-ring-color' : 'none',
		'&:hover': {
			borderColor: 'none'
		}
	}),
	menuList: selectStyle => ({
		...selectStyle,
		padding: 0
	}),
	menu: (selectStyle, state) => ({
		...selectStyle,
		margin: 0,
		borderRadius: 0,
		border: '1px solid #707070',
		borderTop: 'none',
		boxShadow: 'none',
		width: state.isMulti && !isNarrowScreen() ? '350px' : '250px',
		fontSize: '14px',
		zIndex: 2
	}),

	singleValue: selectStyle => ({
		...selectStyle,
		margin: 0
	}),
	indicatorSeparator: selectStyle => ({
		...selectStyle,
		display: 'none'
	}),
	dropdownIndicator: selectStyle => ({
		...selectStyle,
		padding: '6px'
	})
};

export default memo(
	({
		name,
		label,
		index,
		options,
		onChange,
		isMulti = false,
		isPure = true,
		disabled = false,
		subtitleLabel
	}: SelectInputProps) => {
		const intl = useIntl();
		const formik = useFormikContext<Questionnaire>();
		const wasComponentUnmounted = useRef(false);
		const englishCountryOptions = useCountryOptions('en');

		const initiateEmptyValidation = () => {
			formik.setFieldTouched(name);
		};

		const formatOptionLabel = ({ label: labelName, dataCy }: Option) => (
			<div
				data-cy={dataCy?.id && `${dataCy.id.toString().replace('base.', '')}`}
				title={typeof labelName === 'string' ? labelName : undefined}
			>
				{labelName}
			</div>
		);

		const MultiValueRemove = (props: MultiValueProps<SelectInputProps>) => {
			return (
				<components.MultiValueRemove {...props}>
					<div data-cy={`${name}.removeSingleCountry`}>
						<FontAwesomeIcon icon={faTimes} />
					</div>
				</components.MultiValueRemove>
			);
		};

		const CustomClear = () => (
			<div data-cy={`${name}.removeAllMultiSelect`}>
				<FontAwesomeIcon color="#cccccc" icon={faTimes} />
			</div>
		);

		const ClearIndicator = (props: any) => {
			const {
				children = <CustomClear />,
				innerProps: { ref, ...restInnerProps }
			} = props;
			return (
				<div {...restInnerProps} className="remove" ref={ref}>
					{children}
				</div>
			);
		};

		useEffect(() => {
			window.addEventListener('validateEmptyField', initiateEmptyValidation);
			return () => {
				wasComponentUnmounted.current = true;
				formik.setFieldValue(name, undefined, true);
				formik.setFieldTouched(name, false);
				window.removeEventListener('validateEmptyField', initiateEmptyValidation);
			};
		}, []);

		let translatedOptions: Option[];
		if (options.length && options[0].label && typeof options[0].label !== 'string') {
			translatedOptions = options.map(option => ({
				label: intl.formatMessage(option.label as MessageDescriptor),
				value: option.value,
				dataCy: option.label as MessageDescriptor
			}));
		} else {
			translatedOptions = options.map(option => {
				if (option.label && typeof option.label !== 'string') {
					return {
						label: intl.formatMessage(option.label as MessageDescriptor),
						value: option.value,
						dataCy: option.label as MessageDescriptor
					};
				}

				return option;
			});
		}

		if (translatedOptions.length > 100 && !translatedOptions[0].label) {
			translatedOptions = englishCountryOptions;
		}

		useEffect(() => {
			// Validate twice as first time validation does not see field in block.touched
			if (getIn(formik.touched, name)) {
				formik.validateField(name);
			}
		}, [getIn(formik.values, name)]);

		useEffect(() => {
			if (getIn(formik.touched, name)) {
				formik.validateField(name);
			}
		}, [getIn(formik.touched, name)]);

		const Component = isPure ? FastField : Field;
		const { selectInput, styles: themeStyle } = useContext(ThemeContext);

		const validateEmptyField = (value: string) => {
			let errorMessage;
			if (wasComponentUnmounted.current) {
				return errorMessage;
			}
			if (
				(!value || (Array.isArray(value) && value.length === 0)) &&
				getIn(formik.touched, name)
			) {
				errorMessage = intl.formatMessage(messages.requiredField);
			}
			return errorMessage;
		};

		// Filter results by the beginning of any word in an option
		// find beginning of word if it starts the option or is preceded by ' ' or '('
		const filterBeginningOfAnyWord = (option: any, rawInput: any) => {
			const words = rawInput.split(' ');
			return words.reduce((acc: any, cur: any) => {
				return (
					acc &&
					(option.label?.toLowerCase().indexOf(cur?.toLowerCase()) === 0 ||
						option.label
							?.toLowerCase()
							.charAt(option.label?.toLowerCase().indexOf(cur.toLowerCase()) - 1) ===
							' ' ||
						option.label
							?.toLowerCase()
							.charAt(option.label?.toLowerCase().indexOf(cur.toLowerCase()) - 1) ===
							'(')
				);
			}, true);
		};

		return (
			<div className={style.wrapper}>
				{label && (
					<label htmlFor={name} className={classNames(style.label, themeStyle.label)}>
						{`${intl.formatMessage(label)}${index ? ` (${index + 1})` : ''}`}
					</label>
				)}
				{subtitleLabel && (
					<label
						htmlFor={name}
						className={classNames(style.subtitleLabel, themeStyle.subtitleLabel)}
					>
						{`${intl.formatMessage(subtitleLabel)}${index ? ` (${index + 1})` : ''}`}
					</label>
				)}
				<Component name={name} id={name} validate={validateEmptyField}>
					{({ field, form }: FieldProps) => {
						// Set value object from value only
						let value;
						if (isMulti) {
							value =
								field.value &&
								field.value.map((singleValue: string) =>
									translatedOptions.find(option => option.value === singleValue)
								);
						} else {
							value = translatedOptions.find(option => option.value === field.value);
							if (
								field.value &&
								value === undefined &&
								((name.includes('ubo') && name.includes('fullName')) ||
									(name.includes('cms') && name.includes('directorName')) ||
									(name.includes('headAlsoRepresentative') &&
										name.includes('name')))
							) {
								formik.setFieldError(name, messages.UBOAndCMSError.defaultMessage);
								formik.setFieldTouched(name, true, false);
							}
						}

						return (
							<Select
								formatOptionLabel={formatOptionLabel}
								components={{ ClearIndicator, MultiValueRemove }}
								options={translatedOptions}
								filterOption={filterBeginningOfAnyWord}
								id={name}
								name={name}
								onChange={(option: ValueType<Option>) => {
									const optionValue = Array.isArray(option)
										? option.map(singleOption => singleOption.value)
										: option
										? (option as Option).value
										: isMulti
										? []
										: '';
									if (onChange) onChange(optionValue);
									// Force validation so that it is run with the new values
									form.setFieldValue(name, optionValue, true);
									form.setFieldTouched(name, true, false);
								}}
								onBlur={() => {
									form.setFieldTouched(name, true, true);
								}}
								styles={selectInput.styles}
								placeholder=""
								isMulti={isMulti}
								value={value}
								key={value}
								isDisabled={disabled}
								theme={theme => deepmerge(theme, selectInput.theme)}
							/>
						);
					}}
				</Component>
				<ErrorMessage name={name} className={style.error} component="div" />
			</div>
		);
	}
);
